import { observer } from 'mobx-react';
import { createElement, Fragment } from 'react';
import { ICON_BUTTON_ICONS } from '../../../../../components/buttons/IconButton';
import { PUNCTUATION } from '../../../../../utils/constants/notation';
import { calculateAge, getProfileIcon } from '../../../../../utils/utils';
import pubSub from '../../../../../utils/pubsub';
import {
  highlightSearchKey,
  useContextMenuState,
  createKeyboardNavigationHandler
} from '../shared/profilerNameSearchUtils';
import ContextMenuTooltip from '../shared/ContextMenuTooltip';

/**
 * IndividualsNameResultItem - Individual result entry
 *
 * Displays a single individual search result with:
 * - Profile picture on left (PERSON icon fallback)
 * - Individual name (First + Last with search highlighting)
 * - Location (city, country) with GEO_ALT icon
 * - Age (calculated from birthday) with PEOPLE icon
 * - Connected companies list (up to 3) with BUILDING icon - aligned right
 * - Click to navigate to individual profile (no data preload)
 * - Context menu for "open in new tab"
 * - Keyboard navigation
 *
 * @param {Object} result - Individual data (id, firstName, lastName, location, gender, birthday, companies)
 * @param {string} searchKey - Current search key for highlighting
 * @param {function} onNavigateToIndividual - Callback to navigate to individual details
 * @param {Object} utilsStore - Utils store for opening in new tab
 * @param {string} inputId - ID of the search input (for keyboard nav)
 */
const IndividualsNameResultItem = observer(
  ({
    result: individual,
    searchKey,
    onNavigateToIndividual,
    utilsStore,
    inputId = 'ps-input-searchKey'
  }) => {
    const state = useContextMenuState();

    // TD-1709: Navigate directly - no data preload needed
    const handleClick = () => {
      onNavigateToIndividual(individual);
    };

    // Open in new tab using URL-based navigation
    const handleOpenInNewTab = () => {
      onNavigateToIndividual(individual, null, ({ individualId, ref }) => {
        utilsStore.openIndividualDetails(individualId, { ref });
      });
    };

    const handleKeyDown = createKeyboardNavigationHandler(handleClick, inputId);

    // Build full name
    const fullName = [individual.firstName, individual.lastName].filter(Boolean).join(' ');

    // Calculate age from birthday or birth year
    const age = calculateAge(individual.birthday, individual.birthYear);

    // Companies the individual is connected to (up to 3)
    const companies = individual.companies || [];
    const displayCompanies = companies.slice(0, 3);

    return (
      <Fragment>
        <div
          id={`profiler-name-search-result-entry-${individual.id}`}
          className="profiler-name-search-result-entry"
          onContextMenu={(event) => {
            event.preventDefault();
            pubSub.publish({ event: 'icon-button-context-menu-trigger' });
            state.toggleLinkContextMenu();
          }}
          onClick={handleClick}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <div className="info-wrap">
            {/* Profile picture - always sibling to inner-info-wrap for proper alignment */}
            <div className="website-logo-wrap individual-profile-wrap">
              <img
                className="website-logo individual-profile-icon"
                src={getProfileIcon(individual.gender)}
                alt="Profile"
              />
            </div>
            <div className="inner-info-wrap">
              <div className="company-name-wrap">
                <div className="company-name">
                  {highlightSearchKey(fullName, searchKey, createElement)}
                </div>
              </div>
              <div className="info-row">
                {/* Location */}
                {individual.location && (
                  <>
                    <div className="info-row-item" title="Location">
                      <div className="icon">
                        <img src={ICON_BUTTON_ICONS.GEO_ALT} alt="" />
                      </div>
                      <div className="content">{individual.location}</div>
                    </div>
                    <div className="item-separator" />
                  </>
                )}
                {/* Age */}
                {age !== null && (
                  <div className="info-row-item" title="Age">
                    <div className="icon">
                      <img src={ICON_BUTTON_ICONS.AGE} alt="" />
                    </div>
                    <div className="content">{age}</div>
                  </div>
                )}
                {/* Fallback if no location or age */}
                {!individual.location && age === null && (
                  <div className="info-row-item">
                    <div className="content">{PUNCTUATION.EMPTY_VALUE}</div>
                  </div>
                )}
                {/* Connected companies - aligned right */}
                {displayCompanies.length > 0 && (
                  <div className="info-row-item companies individual-companies">
                    {utilsStore.windowWidth < 834 ? (
                      <>
                        <div className="icon">
                          <img src={ICON_BUTTON_ICONS.BUILDING} alt="" />
                        </div>
                        <div className="content companies">
                          {displayCompanies.map((c) => c.name).join(', ')}
                        </div>
                      </>
                    ) : (
                      <div className="desktop-companies-wrap" title="Connected companies">
                        {displayCompanies.map((company, index) => (
                          <div key={company.id || index} className="company-item">
                            <div className="icon">
                              <img src={ICON_BUTTON_ICONS.BUILDING} alt="" />
                            </div>
                            <span className="company-name-text">{company.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {utilsStore.windowWidth < 834 && <div className="company-entry-separator" />}
        {state.toggledLinkContextMenu && (
          <ContextMenuTooltip
            anchorId={`profiler-name-search-result-entry-${individual.id}`}
            onOpenInNewTab={handleOpenInNewTab}
          />
        )}
      </Fragment>
    );
  }
);

export default IndividualsNameResultItem;
